import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  TextField,
  IconButton,
  Typography,
  List,
  Divider,
  Tooltip,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Send,
  Stop,
  Add,
  Delete,
  Menu as MenuIcon,
  HistoryOutlined,
  Share,
  Edit,
  ContentCopy,
  Check,
} from "@mui/icons-material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { copyToClipboard, getTranslation } from "common";
import { useAiStudioChat, useTranslations } from "hooks";
import {
  AiStudioHandoff,
  AiStudioMessage,
  AiStudioPart,
  AiStudioRoute,
  AiStudioSession,
} from "hooks/useAiStudioChat";
import DrawerHeader from "components/aiChat/DrawerHeader";
import ChatBubble from "components/aiChat/ChatBubble";
import MarkdownViewer from "components/aiChat/MarkdownViewer";
import ToolCallCard from "components/aiChat/ToolCallCard";
import RouteBadge from "components/aiChat/RouteBadge";
import RouteChoiceCard from "components/aiChat/RouteChoiceCard";
import BpmnArtifactCard from "components/aiChat/BpmnArtifactCard";
import RobotArtifactCard from "components/aiChat/RobotArtifactCard";
import HandoffCard from "components/aiChat/HandoffCard";
import ShareChatDialog from "components/aiChat/ShareChatDialog";
import RenameChatDialog from "components/aiChat/RenameChatDialog";

dayjs.extend(relativeTime);

/**
 * AI Assistant (internally still "AI Studio": the context, the flow name and
 * the /ai/studio/* prefix are unchanged, renaming them would mean migrating
 * chathistory and ai_runtime_defaults rows for no benefit).
 *
 * Visually this page is the full-page twin of <AiDrawer />: same header bar
 * (the shared DrawerHeader), same rounded "+" icon button, same session
 * cards, same input field with the send button embedded as an endAdornment.
 * The two surfaces are the same product feature, so they must not look like
 * two different apps -- the roundness constants below are the drawer's.
 *
 * What this page has and the drawer does NOT need: a hamburger that collapses
 * the session sidebar. The drawer is itself a panel the user opens and
 * closes; here the sidebar lives inside the page, so it needs its own toggle
 * to give the conversation the full width.
 *
 * Since the intent router, this page also fronts all four specialists: the
 * header chip says which one is answering, an ambiguous question renders a
 * RouteChoiceCard instead of a guess, and a diagram or form schema produced
 * in chat gets a handoff card into its real editor.
 */

// Common roundness for the AI surfaces -- kept identical to AiDrawer's.
const ROUND_FIELD = "20px";
const ROUND_BUTTON = "16px";
const ROUND_ICON = "14px";

const SIDEBAR_WIDTH = 280;

/** How long the copy button stays on the green check, as in the drawer. */
const COPIED_FEEDBACK_MS = 1500;
const MAX_CHAR_LIMIT = 2000;

/**
 * Tool activity is internal plumbing, not an answer. By default the user sees
 * a neutral "consulting" line while a tool runs and nothing at all once it
 * lands -- no tool name, no arguments, no raw JSON. Set
 * REACT_APP_AI_STUDIO_DEBUG_TOOLS=1 to get the full ToolCallCard back while
 * developing against the MCP tools.
 *
 * IMPORTANT: this is presentation only. The tool_call/tool_result parts MUST
 * stay in the message state even when hidden -- useAiStudioChat replays
 * tool_result.content as the `role: "tool"` history on the next turn (see
 * hooks/useAiStudioChat.ts), so the frontend is the source of truth for what
 * the model already fetched. Dropping the parts, or stripping `content` from
 * the SSE frame server-side, would make the assistant forget every tool
 * result from previous turns.
 */
const SHOW_TOOL_CARDS = process.env.REACT_APP_AI_STUDIO_DEBUG_TOOLS === "1";

/** Neutral progress line shown while a tool call has no result yet. */
const ToolActivityIndicator: React.FC = () => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ my: 0.5 }}>
    <CircularProgress size={14} thickness={5} />
    <Typography variant="body2" sx={{ color: "text.secondary" }}>
      Consultando i dati di Flowenti…
    </Typography>
  </Stack>
);

/**
 * Single-line session title that shows a Tooltip with the full text only when
 * it is actually truncated. Same helper AiDrawer uses for its history rows.
 */
const TruncatedTitle: React.FC<{ title: string; isActive: boolean }> = ({
  title,
  isActive,
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el) setTruncated(el.scrollWidth > el.clientWidth);
  }, [title]);

  return (
    <Tooltip title={title} disableHoverListener={!truncated} placement="top">
      <Typography
        ref={ref}
        variant="subtitle2"
        noWrap
        sx={{ fontWeight: isActive ? 600 : 400 }}
      >
        {title}
      </Typography>
    </Tooltip>
  );
};

/** Turns a message's parts into renderable nodes. tool_call parts become a
 * progress line while running (or a ToolCallCard in debug mode) and are
 * matched with the next tool_result of the same name (the backend always
 * emits them adjacently, in call order). */
const renderParts = (
  parts: AiStudioPart[],
  uuid: string,
  onChooseRoute: (route: AiStudioRoute) => void,
  choiceDisabled: boolean,
  onHandoff: (handoff: AiStudioHandoff) => void
) => {
  const nodes: React.ReactNode[] = [];
  let textBuffer = "";
  let flushIdx = 0;

  const flushText = () => {
    if (textBuffer) {
      nodes.push(
        <MarkdownViewer
          key={`${uuid}-text-${flushIdx++}`}
          uuid={`${uuid}-${flushIdx}`}
          message={textBuffer}
        />
      );
      textBuffer = "";
    }
  };

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (part.type === "text") {
      textBuffer += part.text;
    } else if (part.type === "tool_call") {
      const next = parts[i + 1];
      const result =
        next && next.type === "tool_result" && next.name === part.name
          ? { isError: next.isError, content: next.content }
          : undefined;

      if (SHOW_TOOL_CARDS) {
        flushText();
        nodes.push(
          <ToolCallCard
            key={`${uuid}-tool-${i}`}
            name={part.name}
            arguments={part.arguments}
            result={result}
          />
        );
      } else if (!result) {
        // Still running: keep the user informed without naming the tool.
        // Once the result lands this node disappears and only the model's
        // prose remains -- which is where a failure gets explained too.
        flushText();
        nodes.push(<ToolActivityIndicator key={`${uuid}-tool-${i}`} />);
      }

      if (result) i++; // consumed the matching tool_result
    } else if (part.type === "route_choice") {
      flushText();
      nodes.push(
        <RouteChoiceCard
          key={`${uuid}-choice-${i}`}
          question={part.question}
          options={part.options}
          onChoose={onChooseRoute}
          disabled={choiceDisabled}
        />
      );
    } else if (part.type === "handoff") {
      flushText();
      nodes.push(
        <HandoffCard
          key={`${uuid}-handoff-${i}`}
          handoff={part.handoff}
          onOpen={() => onHandoff(part.handoff)}
        />
      );
    } else if (part.type === "artifact") {
      flushText();
      const artifact = part.artifact;
      if (artifact.kind === "bpmn") {
        nodes.push(
          <BpmnArtifactCard
            key={`${uuid}-artifact-${i}`}
            xml={artifact.xml}
            filename={artifact.filename}
          />
        );
      } else if (artifact.kind === "robot_form") {
        nodes.push(
          <RobotArtifactCard
            key={`${uuid}-artifact-${i}`}
            schema={artifact.schema}
            uischema={artifact.uischema}
          />
        );
      } else {
        nodes.push(
          <RobotArtifactCard
            key={`${uuid}-artifact-${i}`}
            code={artifact.code}
            language={artifact.language}
          />
        );
      }
    }
    // standalone tool_result (no preceding tool_call in this slice) is
    // unreachable in practice -- the backend always emits tool_call first.
  }
  flushText();
  return nodes;
};

const AiStudioPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const translations = useTranslations();

  const {
    llmInputRef,
    aiAnswers,
    inflight,
    pendingPrompt,
    stopChat,
    sessions,
    currentSessionId,
    currentIdSession,
    startNewSession,
    loadSession,
    deleteSession,
    renameSession,
    shareSession,
    refreshSessions,
    handleSubmit,
    currentRoute,
    setRoute,
    chooseRoute,
  } = useAiStudioChat();

  const navigate = useNavigate();

  // The sidebar starts collapsed on a phone, where 280px of session list
  // would leave the conversation unreadable.
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [charCount, setCharCount] = useState(0);
  const [loadingSession, setLoadingSession] = useState<string | null>(null);

  // Share-link dialog, same UX as the drawer's: open it first with a
  // spinner, because minting the slug is a round-trip to the backend.
  const [shareDialog, setShareDialog] = useState<{
    open: boolean;
    url: string;
    loading: boolean;
  }>({ open: false, url: "", loading: false });

  const closeShareDialog = () =>
    setShareDialog({ open: false, url: "", loading: false });

  // Rename dialog: we keep the whole session, not just its id, so the field
  // can be seeded with the title the user is about to change.
  const [renameTarget, setRenameTarget] = useState<AiStudioSession | null>(null);

  // Which answer is showing the "copied" checkmark, and the timer that clears
  // it. One at a time: copying a second answer moves the mark, it doesn't
  // leave two bubbles claiming to be on the clipboard.
  const [copiedUuid, setCopiedUuid] = useState<string | null>(null);
  const copyTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
    },
    []
  );

  /**
   * Plain text of an assistant answer: the prose only. Tool cards, choice
   * cards and the handoff card are UI, not something anyone wants pasted
   * into a ticket.
   */
  const answerText = (msg: AiStudioMessage) =>
    msg.parts
      .filter(
        (p): p is Extract<AiStudioPart, { type: "text" }> => p.type === "text"
      )
      .map((p) => p.text)
      .join("");

  /**
   * Copy button under a finished answer, mirroring the drawer's. Absent on
   * errors (there the "answer" is the error line) and while the answer is
   * still being written -- copying half a sentence is a trap.
   *
   * Note `isLoading` is not that signal: it flips to false on the FIRST flush
   * of frames, while tokens are still arriving. The turn is in flight exactly
   * while `inflight` is true, and only the last bubble can be the one
   * streaming.
   */
  const copyFooter = (msg: AiStudioMessage) => {
    if (!msg.isAi || msg.isError || msg.isLoading) return undefined;
    const isStreaming =
      inflight && msg.uuid === aiAnswers[aiAnswers.length - 1]?.uuid;
    if (isStreaming) return undefined;
    const text = answerText(msg);
    if (!text.trim()) return undefined;

    const isCopied = copiedUuid === msg.uuid;
    const label = isCopied
      ? getTranslation(translations, "ai_drawer.copied") || "Copiato"
      : getTranslation(translations, "ai_drawer.copy") || "Copia";

    return (
      <Tooltip title={label} placement="top" enterDelay={400}>
        <IconButton
          size="small"
          aria-label={label}
          onClick={() => handleCopy(msg.uuid, text)}
          sx={{
            borderRadius: ROUND_ICON,
            color: isCopied ? "success.main" : "text.secondary",
            transition: "color 0.2s, background-color 0.2s",
            "&:hover": { color: "primary.main", backgroundColor: "action.hover" },
          }}
        >
          {isCopied ? (
            <Check fontSize="small" />
          ) : (
            <ContentCopy fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    );
  };

  const handleCopy = (uuid: string, text: string) => {
    copyToClipboard(text);
    setCopiedUuid(uuid);
    if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
    copyTimerRef.current = window.setTimeout(() => {
      setCopiedUuid(null);
      copyTimerRef.current = null;
    }, COPIED_FEEDBACK_MS);
  };

  const openShareDialog = async (id: string) => {
    setShareDialog({ open: true, url: "", loading: true });
    const res = await shareSession(id);
    if (!res) {
      // shareSession already surfaced the reason through the snackbar;
      // leaving an empty dialog open would just be a second, mute error.
      closeShareDialog();
      return;
    }
    setShareDialog({ open: true, url: res.url, loading: false });
  };

  // Placeholder shown when the input is empty but a queue message is
  // waiting: shows the first words of the queued prompt so the user
  // remembers what's pending. Truncated to keep the input visually
  // quiet (no full-message preview -- it'd be too wide on mobile).
  const placeholder =
    pendingPrompt && charCount === 0
      ? `Invio al termine: «${pendingPrompt.slice(0, 40)}${
          pendingPrompt.length > 40 ? "…" : ""
        }»`
      : "Scrivi un messaggio...";

  const scrollRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (sessions.length === 0 && !currentSessionId) {
      startNewSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [aiAnswers]);

  useEffect(() => () => stopChat(), []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Leave for the screen that owns this kind of request, carrying the prompt
   * so its own AI drawer can pick the conversation straight back up.
   *
   * Only ever called from the handoff card's button: the Assistant offers the
   * modeller, the user decides. We used to navigate the moment the frame
   * arrived, which yanked people out of a page they were still reading --
   * being right about where the request belongs does not make it our turn to
   * move them.
   */
  const openHandoff = useCallback(
    (handoff: AiStudioHandoff) => {
      navigate(handoff.target, {
        state: { aiPrompt: handoff.prompt, openAi: true },
      });
    },
    [navigate]
  );

  const canSend = charCount > 0 && charCount <= MAX_CHAR_LIMIT;

  const submit = () => {
    if (!canSend) return;
    handleSubmit();
    setCharCount(0);
  };

  const handleNewChat = () => {
    startNewSession();
    if (llmInputRef.current) llmInputRef.current.value = "";
    setCharCount(0);
  };

  const handleLoadSession = async (session: AiStudioSession) => {
    setLoadingSession(session.id);
    try {
      await loadSession(session.id);
    } finally {
      setLoadingSession(null);
    }
    if (isMobile) setSidebarOpen(false);
  };

  const formatRelativeTime = (timestamp: number): string => {
    try {
      return dayjs(timestamp).fromNow();
    } catch {
      return "";
    }
  };

  return (
    <Stack direction="row" sx={{ height: "100%", minHeight: 0 }}>
      {/* Session sidebar -- collapsed by the hamburger in the header. The
          width is animated instead of unmounting the list, so reopening it
          doesn't re-run the row measurements (TruncatedTitle). */}
      <Box
        sx={{
          width: sidebarOpen ? SIDEBAR_WIDTH : 0,
          flexShrink: 0,
          overflow: "hidden",
          borderRight: (t) => (sidebarOpen ? `1px solid ${t.palette.divider}` : "none"),
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          display: "flex",
          flexDirection: "column",
          backgroundColor: "background.paper",
        }}
      >
        <Box
          sx={{
            width: SIDEBAR_WIDTH,
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <DrawerHeader sx={{ px: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Le tue chat
            </Typography>
            <Tooltip title="Aggiorna elenco">
              <IconButton
                size="small"
                onClick={() => refreshSessions()}
                aria-label="Aggiorna elenco"
                sx={{ borderRadius: ROUND_ICON }}
              >
                <HistoryOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </DrawerHeader>

          <Box
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              overflow: "auto",
            }}
          >
            {sessions.length === 0 ? (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", textAlign: "center", mt: 4 }}
              >
                Nessuna chat, ancora.
              </Typography>
            ) : (
              <List sx={{ display: "flex", flexDirection: "column", gap: 1, p: 0 }}>
                {sessions
                  .slice()
                  .sort((a, b) => b.timestamp - a.timestamp)
                  .map((session) => (
                    <Box
                      key={session.id}
                      onClick={() => handleLoadSession(session)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 1.5,
                        borderRadius: ROUND_FIELD,
                        border: "1px solid",
                        borderColor: (t) =>
                          session.id === currentSessionId
                            ? "primary.light"
                            : t.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.16)"
                              : t.palette.grey[200],
                        backgroundColor: (t) =>
                          session.id === currentSessionId
                            ? t.palette.mode === "dark"
                              ? "rgba(84, 189, 193, 0.14)"
                              : "primary.50"
                            : "background.paper",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": {
                          backgroundColor: (t) =>
                            t.palette.mode === "dark"
                              ? "rgba(84, 189, 193, 0.10)"
                              : t.palette.grey[50],
                          transform: "translateY(-1px)",
                          boxShadow: (t) =>
                            t.palette.mode === "dark"
                              ? "0 2px 10px rgba(0, 0, 0, 0.35)"
                              : "0 2px 8px rgba(0,0,0,0.05)",
                        },
                      }}
                    >
                      <Box sx={{ flex: 1, overflow: "hidden", mr: 1 }}>
                        <TruncatedTitle
                          title={session.title || "Nuova chat"}
                          isActive={session.id === currentSessionId}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          {formatRelativeTime(session.timestamp)}
                        </Typography>
                      </Box>
                      {loadingSession === session.id ? (
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                      ) : null}
                      <Tooltip title="Condividi chat">
                        <IconButton
                          size="small"
                          aria-label="Condividi chat"
                          onClick={(e) => {
                            // Without this the click also selects the card
                            // and loads the conversation behind the dialog.
                            e.stopPropagation();
                            openShareDialog(session.id);
                          }}
                          sx={{
                            borderRadius: ROUND_ICON,
                            color: "text.secondary",
                            "&:hover": { color: "primary.main" },
                          }}
                        >
                          <Share fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Rinomina chat">
                        <IconButton
                          size="small"
                          aria-label="Rinomina chat"
                          onClick={(e) => {
                            e.stopPropagation();
                            setRenameTarget(session);
                          }}
                          sx={{
                            borderRadius: ROUND_ICON,
                            color: "text.secondary",
                            "&:hover": { color: "primary.main" },
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        aria-label="Elimina chat"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteSession(session.id);
                        }}
                        sx={{
                          borderRadius: ROUND_ICON,
                          color: "text.secondary",
                          "&:hover": { color: "error.main" },
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
              </List>
            )}
          </Box>
        </Box>
      </Box>

      {/* Chat panel */}
      <Stack
        sx={{
          flex: 1,
          minWidth: 0,
          height: "100%",
          backgroundColor: "background.paper",
        }}
      >
        <DrawerHeader sx={{ justifyContent: "flex-start", pl: isMobile ? 2 : 3, pr: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {/* The one control the drawer doesn't need: the sidebar is part
                of this page, so it gets its own collapse toggle. */}
            <Tooltip title={sidebarOpen ? "Nascondi le chat" : "Mostra le chat"}>
              <IconButton
                size="small"
                onClick={() => setSidebarOpen((open) => !open)}
                aria-label={sidebarOpen ? "Nascondi le chat" : "Mostra le chat"}
                sx={{ borderRadius: ROUND_ICON }}
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Nuova chat">
              <IconButton
                size="small"
                onClick={handleNewChat}
                aria-label="Nuova chat"
                sx={{ borderRadius: ROUND_ICON }}
              >
                <Add />
              </IconButton>
            </Tooltip>

            {/* Share the conversation on screen. Hidden (not disabled) until
                the session exists on the backend: a chat with no id cannot be
                shared, and a permanently greyed-out button on an empty page
                just invites clicks that do nothing. */}
            {currentIdSession ? (
              <Tooltip title="Condividi chat">
                <IconButton
                  size="small"
                  onClick={() => openShareDialog(currentSessionId)}
                  aria-label="Condividi chat"
                  sx={{ borderRadius: ROUND_ICON }}
                >
                  <Share />
                </IconButton>
              </Tooltip>
            ) : null}

            {/* The mode chip sits with the "+" rather than pinned to the far
                right: it belongs to the conversation you are about to start,
                so it reads as part of the same group of controls. */}
            <RouteBadge route={currentRoute} onChange={setRoute} />
          </Stack>
        </DrawerHeader>
        <Divider />

        <List
          ref={scrollRef}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            overflowX: "hidden",
            py: 2,
            px: isMobile ? 2 : 4,
            // The global theme (MuiCssBaseline) shrinks every scrollbar to
            // 5px; the drawer widens it back for its message list and this
            // panel is the same surface, so it does too.
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(0,0,0,0.2)",
              borderRadius: "10px",
            },
          }}
        >
          {aiAnswers.length === 0 && (
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={1.5}
              sx={{ height: "100%", color: "text.secondary" }}
            >
              <AutoAwesomeIcon fontSize="large" color="primary" />
              <Typography
                variant="h6"
                sx={{ color: "primary.main", fontWeight: 700, letterSpacing: 1 }}
              >
                AI Assistant
              </Typography>
              <Typography variant="body2" sx={{ maxWidth: 460, textAlign: "center" }}>
                Posso guidarti nell'app, rispondere sui tuoi dati, modellare un
                processo o generare un robot — scrivi pure, capisco io dove
                portarti.
              </Typography>
            </Stack>
          )}
          {aiAnswers.map((msg: AiStudioMessage) =>
            msg.hidden ? null : (
              <ChatBubble
                key={msg.uuid}
                isAi={msg.isAi}
                footer={copyFooter(msg)}
              >
                {msg.isLoading && msg.parts.length === 0 ? (
                  <CircularProgress size={18} thickness={5} />
                ) : msg.isError ? (
                  <Typography variant="body2" color="error.main">
                    {msg.parts.find((p) => p.type === "text")?.type === "text"
                      ? (msg.parts.find((p) => p.type === "text") as Extract<AiStudioPart, { type: "text" }>).text
                      : "Si è verificato un errore."}
                  </Typography>
                ) : (
                  <Stack spacing={0.5}>
                    {renderParts(
                      msg.parts,
                      msg.uuid,
                      (route) => chooseRoute(msg.uuid, route),
                      inflight,
                      openHandoff
                    )}
                  </Stack>
                )}
              </ChatBubble>
            )
          )}
        </List>

        {/* Composer -- the drawer's field: the send/stop button lives inside
            the input as an endAdornment, with the character counter below. */}
        <Box sx={{ pt: 1, pb: isMobile ? 3 : 2, px: 2 }}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <TextField
              inputRef={llmInputRef}
              size="small"
              fullWidth
              multiline
              maxRows={10}
              minRows={1}
              placeholder={placeholder}
              onChange={(e) => setCharCount(e.target.value.length)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey) {
                  e.preventDefault();
                  submit();
                }
              }}
              InputProps={{
                endAdornment: inflight ? (
                  <Tooltip title="Ferma" placement="top">
                    <span>
                      <IconButton
                        color="error"
                        onClick={stopChat}
                        aria-label="Ferma"
                        size="small"
                        sx={{
                          border: "1px solid",
                          borderColor: "error.light",
                          borderRadius: ROUND_BUTTON,
                          p: 1,
                          ml: 0.5,
                          "&:hover": { backgroundColor: "error.50" },
                        }}
                      >
                        <Stop fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip title="Invia" placement="top">
                    <span>
                      <IconButton
                        type="submit"
                        aria-label="Invia"
                        disabled={!canSend}
                        size="small"
                        color="primary"
                        sx={{
                          borderRadius: ROUND_ICON,
                          ml: 0.5,
                          backgroundColor: canSend ? "primary.main" : "transparent",
                          color: canSend ? "common.white" : "text.disabled",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: canSend ? "primary.dark" : "transparent",
                          },
                        }}
                      >
                        <Send fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                ),
                sx: {
                  fontSize: "15px",
                  lineHeight: "1.5",
                  py: 1.25,
                  px: 1.75,
                  pr: 1,
                  borderRadius: ROUND_FIELD,
                  backgroundColor: "background.paper",
                  transition: "all 0.2s ease",
                  maxHeight: "200px",
                  overflowY: "auto",
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  paddingTop: "10px",
                  paddingBottom: "10px",
                  borderRadius: ROUND_FIELD,
                  "& fieldset": {
                    borderWidth: "1.5px",
                    borderColor: "divider",
                  },
                  "&:hover fieldset": {
                    borderColor: "primary.light",
                    borderWidth: "1.5px",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "primary.main",
                    borderWidth: "2px",
                    boxShadow: "0 0 0 4px rgba(84, 189, 193, 0.12)",
                  },
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "left",
                mt: 0.75,
                ml: 1.5,
                color: charCount > MAX_CHAR_LIMIT ? "error.main" : "text.secondary",
                fontSize: "11px",
                fontWeight: charCount > MAX_CHAR_LIMIT ? 700 : 400,
                transition: "color 0.2s",
              }}
            >
              {charCount}/{MAX_CHAR_LIMIT}
            </Typography>
          </form>
        </Box>
      </Stack>

      <ShareChatDialog
        open={shareDialog.open}
        url={shareDialog.url}
        loading={shareDialog.loading}
        onClose={closeShareDialog}
      />

      <RenameChatDialog
        open={Boolean(renameTarget)}
        initialTitle={renameTarget?.title || ""}
        onClose={() => setRenameTarget(null)}
        onConfirm={(title) => {
          if (renameTarget) renameSession(renameTarget.id, title);
        }}
      />
    </Stack>
  );
};

export default AiStudioPage;
